/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description application store
 */
import _ from 'lodash';
import log from 'loglevel';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { AxiosResponse } from 'axios';

import api from 'client/index';
import menuConfig from 'configs/menu';
import { getUserFromToken, validateUserAccess, shouldInitializeApp } from 'utils/app';
import prepareMenuConfig from 'utils/menu';
import { WORKFLOW_STATUS } from 'app-constants/workflow';
import { CUSTOMER_ARCHETYPE, CUSTOMER_TYPE } from '@veritable/common/constants';

const defaultSetupInfo: App.SetupInfo = {
	timezone: '',
	supportEmail: '',
	stripeIdentityToken: '',
	testFormURL: '',
	version: '',
	releaseVersion: '',
	selectedApps: [],
	quickPay: { link: '', qrCode: '' },
	veritableOnboarding: { status: WORKFLOW_STATUS.completed },
	customerType: [CUSTOMER_TYPE.billing],
	webAutomation: false,
	customerArchetype: CUSTOMER_ARCHETYPE.archetype2,
	isBillingCustomer: true,
};

const defaultMenuConfig: App.IMenuConfig = { data: [], menus: [], pages: [], search: [], openKeys: [] };

const defaultFeatures = {} as App.Features;

const initialState: App.IState = {
	status: {
		loading: true,
		error: false,
		underMaintenance: false,
		message: 'Initializing ... ',
		accessDenied: false,
	},
	user: getUserFromToken(),
	userAccess: validateUserAccess([], defaultSetupInfo.customerType[0]),
	setupInfo: defaultSetupInfo,
	appConfig: defaultMenuConfig,
	settingsConfig: defaultMenuConfig,
	collapsed: true,
	selectedMenu: null,
	features: defaultFeatures,
};

const useAppStore = create<App.IStore>()(
	persist(
		devtools(
			(set) => ({
				...initialState,
				actions: {
					setStatus: (status) => set({ status }),
					setUser: (user) => set({ user }),
					setUserAccess: (userAccess) => set({ userAccess }),
					setSetupInfo: (setupInfo) => set({ setupInfo }),
					setAppConfig: (appConfig) => set({ appConfig }),
					setSettingsConfig: (settingsConfig) => set({ settingsConfig }),
					setCollapsed: (collapsed) => set({ collapsed }),
					setSelectedMenu: (selectedMenu) => set({ selectedMenu }),
					setFeatures: (features) => set({ features }),
				},
			}),
			{ name: 'App Store' },
		),
		{
			name: 'app-store',
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				collapsed: state.collapsed,
			}),
		},
	),
);

// Store to return actions and values
const useAppStoreValues = (): App.IState => useAppStore(useShallow((state) => _.omit(state, ['actions'])));

const useAppStoreActions = (): App.IActions => useAppStore(useShallow((state) => state.actions));

/**
 * Initialize the app
 * @returns {Promise<void>}
 */
const initialize = async (user: App.User): Promise<void> => {
	const { actions, status } = useAppStore.getState();
	const { canAccessApp } = shouldInitializeApp(user?.realmRoles);
	actions.setUser(user);

	if (canAccessApp) {
		try {
			actions.setStatus({ ...status, message: 'Loading settings ...' });
			const [{ data: setupInfo }, { data: features }] = (await Promise.all([
				api.InitialSetupInformationApi.initialSetupInfo(),
				api.FeaturesApi.getFeatureConfig(),
			])) as [AxiosResponse<App.SetupInfo>, AxiosResponse<App.Features>];
			actions.setFeatures(features);
			const isBillingCustomer = setupInfo?.customerArchetype === CUSTOMER_ARCHETYPE.archetype2;
			const customerType = isBillingCustomer ? CUSTOMER_TYPE.billing : CUSTOMER_TYPE.eligibility;
			const updatedSetupInfo = {
				...setupInfo,
				isBillingCustomer,
				customerType: [customerType],
			};
			const userAccess = validateUserAccess(user?.realmRoles, customerType);

			actions.setUserAccess(userAccess);
			actions.setSetupInfo(updatedSetupInfo);
			actions.setAppConfig(
				prepareMenuConfig({
					setupInfo: updatedSetupInfo,
					config: menuConfig.app(customerType, features),
					userRoles: user.realmRoles,
					isRootSection: true,
				}),
			);
			actions.setSettingsConfig(
				prepareMenuConfig({
					setupInfo: updatedSetupInfo,
					config: menuConfig.settings(customerType, features),
					userRoles: user.realmRoles,
					isRootSection: true,
					isSettingRoute: true,
				}),
			);
			actions.setStatus({ ...status, loading: false, message: 'Success! Welcome back to Veritable' });
		} catch (e: AnyType) {
			log.error(e);
			actions.setStatus({ ...status, loading: false, underMaintenance: true, message: 'Site under maintenance' });
		}
	} else {
		actions.setStatus({ ...status, loading: false, accessDenied: true, message: 'Access denied' });
	}
};

export { initialize, useAppStoreValues, useAppStoreActions };
export default useAppStore;
